/**
 * Survey field validation limits.
 * These are the SINGLE SOURCE OF TRUTH for all validation limits.
 * Used by ZOD schemas, surveyConfig validation, and UI components.
 */

export interface FieldLimit {
  min?: number;
  max: number;
}

/**
 * Validation limits for survey array fields.
 * Change limits here and they propagate to ZOD, surveyConfig, and UI.
 */
export const SURVEY_FIELD_LIMITS = {
  // Child Profile - array fields
  personalityTraits: { min: 0, max: 5 },
  interests: { max: 4 },

  // Story Preferences - array fields
  moralLessons: { min: 1, max: 2 },
  photoUrls: { max: 10 },

  // Companion Preferences - array fields
  realWorldSidekicks: { max: 3 },
} as const;

/**
 * Page count limits per book type.
 * Digital/softcover use standard ranges, hardcover requires more pages (Lulu casewrap minimum).
 */
export const PAGE_COUNT_LIMITS = {
  digital_only: { min: 4, max: 32 },
  softcover: { min: 4, max: 32 },
  hardcover: { min: 24, max: 48 },
} as const;

export type PageCountBookType = keyof typeof PAGE_COUNT_LIMITS;

export function getPageCountLimits(bookType?: string): {
  min: number;
  max: number;
} {
  if (
    bookType &&
    Object.prototype.hasOwnProperty.call(PAGE_COUNT_LIMITS, bookType)
  ) {
    return PAGE_COUNT_LIMITS[bookType as PageCountBookType];
  }
  return PAGE_COUNT_LIMITS.digital_only;
}

/** Type-safe field names for survey limits */
export type SurveyFieldName = keyof typeof SURVEY_FIELD_LIMITS;

// ---------------------------------------------------------------------------
// Text field constraints
// ---------------------------------------------------------------------------

export interface TextFieldConstraint {
  min?: number;
  max: number;
  pattern?: RegExp;
  patternMessage?: string;
}

/**
 * Text field constraints for all free-text survey fields.
 * SINGLE SOURCE OF TRUTH — consumed by Zod schemas and UI components.
 */
export const TEXT_FIELD_CONSTRAINTS = {
  // Child Profile
  childName: { min: 2, max: 40 },
  nickname: { min: 2, max: 40 },
  pronouns: {
    min: 1,
    max: 20,
    pattern: /^[\p{L}\p{M} /\-']+$/u,
    patternMessage:
      'Pronouns may only contain letters, spaces, slashes, and hyphens',
  },

  // Story Preferences
  theme: { min: 2, max: 500 },
  plot: { min: 10, max: 4000 },
  additionalDetails: { max: 4000 },
  pageBorderStyle: { max: 200 },

  // Photo
  photoCaption: { max: 140 },

  // Companion (real-world sidekick)
  companionName: { min: 2, max: 40 },
  companionRelationship: { min: 2, max: 40 },
  companionPetType: { min: 2, max: 30 },
  companionDescription: { max: 200 },
} as const satisfies Record<string, TextFieldConstraint>;

/** Type-safe field names for text constraints */
export type TextFieldName = keyof typeof TEXT_FIELD_CONSTRAINTS;

/**
 * Get the text field constraint for a given field name.
 */
export function getTextFieldConstraint(
  fieldName: TextFieldName
): TextFieldConstraint {
  return TEXT_FIELD_CONSTRAINTS[fieldName];
}

/**
 * Get the validation limits for a survey field.
 */
export function getFieldLimits(fieldName: SurveyFieldName): FieldLimit {
  return SURVEY_FIELD_LIMITS[fieldName];
}

/**
 * Generate "Pick up to X" or "Pick X-Y" text for UI display.
 * @example getPickUpToText('interests') => "Pick up to 4"
 * @example getPickUpToText('moralLessons') => "Pick up to 2"
 */
export function getPickUpToText(fieldName: SurveyFieldName): string {
  const limits = SURVEY_FIELD_LIMITS[fieldName];
  const min = 'min' in limits ? limits.min : undefined;
  if (min && min > 1) {
    return `Pick ${min}-${limits.max}`;
  }
  return `Pick up to ${limits.max}`;
}

/**
 * Clamp and round a page count to the valid range for a purchase type.
 * Use this to normalize user input before passing to calculatePrice, descriptions, or metadata.
 */
export function normalizePageCount(
  purchaseType: PageCountBookType,
  pageCount: number
): number {
  const limits =
    PAGE_COUNT_LIMITS[purchaseType] ?? PAGE_COUNT_LIMITS.digital_only;
  return Math.round(Math.min(limits.max, Math.max(limits.min, pageCount)));
}
